<template>
    <v-layer :config="position">
        <v-circle
            :config="{
                radius,
                fill: 'black',
                shadowColor: 'black',
                shadowBlur: 50,
                shadowOpacity: 0.3,
            }"
        />
        <v-group ref="wheel">
            <Wedge
                v-for="(wedge, i) in wedges"
                :ref="el => { wedgeElements[i] = el }"
                :key="i"
                :color="wedge.color"
                :img="wedge.img"
                :rotation="i * angle"
                :angle="angle"
                :radius="radius"
            />
        </v-group>
        <v-group ref="spinButton" @click="startSpin" @touchstart="startSpin">
            <v-circle
                :config="{
                    radius: buttonSize,
                    shadowColor: 'black',
                    shadowBlur: 10,
                    shadowOpacity: 0.5,
                    fillLinearGradientStartPoint: {x: -buttonSize, y: 0},
                    fillLinearGradientStartRadius: 0,
                    fillLinearGradientEndPoint: {x: buttonSize, y: 0},
                    fillLinearGradientColorStops: [0, '#9c6ade', 1, '#6c37b7'],
                }"
            />
            <v-circle
				v-if="buttonSize > 48"
                :config="{
                    y: -20,
                    radius: buttonSize,
                    fillRadialGradientStartPoint: {x: -20, y: -20},
                    fillRadialGradientStartRadius: 0,
                    fillRadialGradientEndPoint: {x: 0, y: 0},
                    fillRadialGradientEndRadius: buttonSize - 20,
                    fillRadialGradientColorStops: [0, 'rgba(255,255,255,0.4)', 1, 'rgba(255,255,255,0)'],
                }"
            />
            <v-image
                :config="{
                    x: -42,
                    y: -12,
                    image: spinLabelImageObj,
                    width: 84,
                    height: 29,
                }"
            />
        </v-group>
    </v-layer>
</template>

<script>
/* eslint-disable */
import Konva from 'konva'
import { ref, computed, watch, onBeforeUpdate, onMounted } from 'vue'

import Wedge from './Wedge.vue'
import { addColor } from './config'

const START_VELOCITY = 12
const START_FRICTION = 0.03
const MAX_FRICTION = 0.4

const randomNumber = (min=0, max=10) => {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}



export default {
    name: "Wheel",
    components: { Wedge },
    props: {
        radius: {
            type: Number,
            default: undefined,
        },
        position: {
            type: Object,
            default: undefined,
        },
        items: {
            type: Array,
            default: () => ([]),
        },
    },
    setup(props, { emit }) {
        const wedges = computed(() => addColor(props.items))
		const angle = computed(() => 360 / wedges.value.length)

        const wheel = ref()

        let wheelRotation = 0
        let prevWheelRotation = 0
        let velocity = 0
        let friction = 0
        let frictionLessSpinningTime = 0
        let pointer = undefined
        let pointerTimeout = undefined
        let pointerResetTween = undefined
        let isTweening = false
		let isHitTweening = false

		const rect = {
			minX: props.position.x - 5,
			maxX: props.position.x + 5,
			minY: props.position.y - props.radius + 12 - 5,
			maxY: props.position.y - props.radius + 12 + 5,
		}

		let pointerHitTween = undefined
		const setPointerHitTween = (duration, rotation) => {
			pointerHitTween = new Konva.Tween({
				node: pointer,
				duration,
				rotation,
				easing: Konva.Easings.EaseOut,
				onFinish: () => {
					isHitTweening = false
					if (!isOnBolt()) {
						pointerResetTween.reset()
						pointerResetTween.play()
					}
				}
			}).play()
		}

		const isOnBolt = () => {
			return wedgeElements.value
						.filter(Boolean)
						.map(wedge => {
							return wedge.bolts.filter(Boolean).map(boltRef => isInRect(boltRef.getNode().absolutePosition(), rect)).some(Boolean)
						})
						.some(Boolean)
		}

        onMounted(() => {
            const stageNode = wheel.value.getNode().parent.parent
           
            setTimeout(() => {
                pointer = stageNode.find('.pointer')[0]
                
				if (isOnBolt()) {
					pointer.rotation(-12)
				}

				pointerResetTween = new Konva.Tween({
					node: pointer,
					duration: 1,
					rotation: 0,
					easing: Konva.Easings.ElasticEaseOut,
					onFinish: () => {
						isTweening = false
					}
				})
            }, 100)
        })

        const spinAnimation = new Konva.Animation(frame => {
            if (frame.time > frictionLessSpinningTime) {
                const frictionDiff = (friction * frame.timeDiff) / 10000;
                friction += frictionDiff + frame.time / 10000000
                if (friction > MAX_FRICTION) friction = MAX_FRICTION
                const velocityDiff = (velocity * frame.timeDiff * (1 - friction)) / 1000;
                velocity = velocity - velocityDiff
            }
            prevWheelRotation = wheelRotation
            wheelRotation += velocity
            wheel.value.getNode().rotation(wheelRotation)

            if (isOnBolt() || velocity > 0.9) {
				if (pointerTimeout) clearTimeout(pointerTimeout)
				if (pointerResetTween) {
					pointerResetTween.pause()
					pointerResetTween.reset()
				}
				isTweening = false
				const rand = randomNumber(20, 35)
				const rot = -Math.max(
					velocity < 1 ? rand * velocity : rand,
					12
				)
				if (velocity < 0.2) {
					if (!isHitTweening) {
						isHitTweening = true
						if (pointerHitTween) {
							pointerHitTween.pause()
							pointerHitTween.reset()
						}
						const duration = Math.max(1 * (velocity * 2), 0.1)
						pointer.rotation(0)
						setPointerHitTween(duration, rot)
					}
				} else {
					pointer.rotation(rot)
				}
            } else {
                if (!isTweening) {
                    isTweening = true
					if (!isHitTweening) {
						pointerResetTween.reset()
						pointerResetTween.play()
					}
                }
            }
            

            if (wheelRotation - prevWheelRotation < 0.1) {
                spinAnimation.stop()
                isSpinning.value = false
            }
        });

        const isSpinning = ref(false)
        const startSpin = () => {
            if (isSpinning.value) {
                spinAnimation.stop()
                isSpinning.value = false
                return
            }
            isSpinning.value = true
            frictionLessSpinningTime = randomNumber(500, 1500)
            friction = START_FRICTION
            velocity = START_VELOCITY
            spinAnimation.start()
        }

        const buttonSize = computed(() => Math.round(props.radius / 5))

        const spinLabelImageObj = new Image();
        spinLabelImageObj.src = '/spin.svg';

        const wedgeElements = ref([]);
        onBeforeUpdate(() => {
            wedgeElements.value = [];
        });

        const isInRect = (pos, rect) => {
            return pos.x >= rect.minX && pos.x <= rect.maxX && pos.y >= rect.minY && pos.y <= rect.maxY
        }

		onMounted(() => {
			emit('mounted')
		})

        return {
            angle,
            wedges,
            startSpin,
            wheel,
            buttonSize,
            spinLabelImageObj,
            wedgeElements,
        }
    },
};
</script>
